import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert, AlertTitle, Box, Button, Checkbox, Chip, CircularProgress, Dialog,
  DialogActions, DialogContent, DialogTitle, Divider, FormControlLabel,
  IconButton, LinearProgress, Link, MenuItem, Paper, Slider, Stack, Switch,
  Tab, Tabs, TextField, Tooltip, Typography,
} from '@mui/material';
import {
  Close, Movie, RestartAlt, Send, Subtitles, Tune, Warning,
} from '@mui/icons-material';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  shortsAPI, ShortsRenderJob, ShortsRenderParams, ShortsShot, ShortsStill,
  ShortsWord,
} from '../../services/api';
import TranscriptScriptEditor from './TranscriptScriptEditor';
import {
  clamp, clampScale, cropRect, dragToPos, framingLevel, COVER_MIN_SCALE,
  FRAMING_LABEL, SCALE_MAX,
} from './shortsFraming';

interface Props {
  clipId: string | null;
  onClose: () => void;
}

/**
 * Width of the 9:16 crop, as a fraction of a 16:9 source frame, at scale 100%
 * — (9/16) / (16/9) = 81/256. At 100% the source height exactly fills the
 * output height, so only this much of its width is visible.
 */
const CROP_W_AT_100 = 81 / 256;

/** Stills are a 3-panel contact sheet: the shot's start, middle and end. */
const PANELS = ['start', 'middle', 'end'] as const;

const fmtTime = (s: number) => {
  if (!Number.isFinite(s)) return '–';
  const m = Math.floor(s / 60);
  return `${m}:${(s - m * 60).toFixed(2).padStart(5, '0')}`;
};

// ── the framing guide ───────────────────────────────────────────────────────

interface PhoneFrameProps {
  still?: ShortsStill;
  posX: number;
  posY: number;
  scale: number;
  onChange: (posX: number, posY: number) => void;
  disabled?: boolean;
  /** Fix the panel (start|middle|end) instead of letting this frame choose. */
  panel?: number;
  compact?: boolean;
}

/**
 * The output as it will look, in a phone-shaped window — REAPER's metaphor
 * (portal doc §24.2), not ours.
 *
 * The user grabs the PICTURE and moves it; there is no crop-box overlay
 * anywhere. Dragging right moves the person right. Our first version drew a
 * box over the uncropped still and dragged that, which is the opposite feel
 * for the same numbers, and is the kind of mismatch that makes someone who
 * uses both tools wrong half the time.
 *
 * The still is a 3-panel contact sheet of the uncropped source, so the image
 * is scaled and offset so that cropRect() exactly fills this 9:16 window.
 */
const PhoneFrame: React.FC<PhoneFrameProps> = ({
  still, posX, posY, scale, onChange, disabled, panel = 1, compact,
}) => {
  const ref = useRef<HTMLDivElement | null>(null);
  const drag = useRef<{ x: number; y: number; px: number; py: number } | null>(null);
  const r = cropRect(posX, posY, scale);

  const onPointerDown = (e: React.PointerEvent) => {
    if (disabled) return;
    (e.target as HTMLElement).setPointerCapture?.(e.pointerId);
    drag.current = { x: e.clientX, y: e.clientY, px: posX, py: posY };
  };
  const onPointerMove = (e: React.PointerEvent) => {
    const d = drag.current;
    const el = ref.current;
    if (disabled || !d || !el) return;
    const box = el.getBoundingClientRect();
    if (!box.width || !box.height) return;
    // REAPER: posX += dx / previewWidth. Grab the picture, not a box.
    const next = dragToPos(d.px, d.py,
      (e.clientX - d.x) / box.width, (e.clientY - d.y) / box.height);
    onChange(next.posX, next.posY);
  };
  const onPointerUp = () => { drag.current = null; };

  return (
    <Box
      ref={ref}
      sx={{
        position: 'relative', aspectRatio: '9 / 16',
        width: compact ? 104 : '100%', maxWidth: compact ? 104 : 230,
        overflow: 'hidden', borderRadius: 1.5, bgcolor: 'grey.900',
        cursor: disabled ? 'default' : 'grab', touchAction: 'none',
        userSelect: 'none', flexShrink: 0,
        border: '1px solid', borderColor: 'divider',
      }}
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={onPointerUp}
      onPointerCancel={onPointerUp}
    >
      {still?.url ? (
        <Box
          component="img"
          src={still.url}
          alt={`${still.camera} frame`}
          draggable={false}
          sx={{
            position: 'absolute',
            // Scale the 3-panel sheet so this shot's crop fills the window.
            width: `${(3 / (r.width || 1)) * 100}%`,
            height: `${(1 / (r.height || 1)) * 100}%`,
            left: `${-((panel + r.left) / (r.width || 1)) * 100}%`,
            top: `${-(r.top / (r.height || 1)) * 100}%`,
            maxWidth: 'none', pointerEvents: 'none',
          }}
        />
      ) : (
        <Stack sx={{ height: '100%' }} alignItems="center" justifyContent="center">
          <Typography variant="caption" color="grey.500" align="center">
            no still
          </Typography>
        </Stack>
      )}

      {/* Rule of thirds, as REAPER draws on every preview. */}
      {[33.333, 66.667].map((v) => (
        <React.Fragment key={v}>
          <Box sx={{ position: 'absolute', left: 0, right: 0, top: `${v}%`,
                     borderTop: '1px solid rgba(255,255,255,0.28)',
                     pointerEvents: 'none' }} />
          <Box sx={{ position: 'absolute', top: 0, bottom: 0, left: `${v}%`,
                     borderLeft: '1px solid rgba(255,255,255,0.28)',
                     pointerEvents: 'none' }} />
        </React.Fragment>
      ))}
    </Box>
  );
};

/** start | middle | end of one shot, all at the same framing — REAPER's row.
 *  This is how someone leaning out of frame mid-shot gets caught. */
const ShotStrip: React.FC<{
  still?: ShortsStill; posX: number; posY: number; scale: number;
  onChange: (posX: number, posY: number) => void; disabled?: boolean;
}> = ({ still, posX, posY, scale, onChange, disabled }) => (
  <Stack direction="row" spacing={0.75}>
    {[0, 1, 2].map((panel) => (
      <Box key={panel}>
        <PhoneFrame still={still} posX={posX} posY={posY} scale={scale}
                    onChange={onChange} disabled={disabled}
                    panel={panel} compact />
        <Typography variant="caption" color="text.secondary"
                    sx={{ display: 'block', textAlign: 'center' }}>
          {['start', 'middle', 'end'][panel]}
        </Typography>
      </Box>
    ))}
  </Stack>
);

// ── numeric controls ────────────────────────────────────────────────────────

interface FramingControlsProps {
  posX: number;
  posY: number;
  scale: number;
  onChange: (patch: { posX?: number; posY?: number; scale?: number }) => void;
  disabled?: boolean;
}

const FramingControls: React.FC<FramingControlsProps> = ({
  posX, posY, scale, onChange, disabled,
}) => (
  <Stack spacing={1.5}>
    {([
      ['posX', posX, 0, 1, 0.0005],
      ['posY', posY, 0, 1, 0.0005],
      // REAPER clamps up to cover-fill after every change, so a lower
      // value here would be a framing it cannot reproduce.
      ['scale', scale, Math.round(COVER_MIN_SCALE * 100) / 100, SCALE_MAX, 0.5],
    ] as const).map(([key, value, min, max, step]) => (
      <Box key={key}>
        <Stack direction="row" alignItems="center" spacing={1}>
          <Typography variant="caption" sx={{ width: 42, fontFamily: 'monospace' }}>
            {key}
          </Typography>
          <Slider
            size="small"
            value={Number.isFinite(value) ? value : min}
            min={min}
            max={max}
            step={step}
            disabled={disabled}
            onChange={(_, v) => onChange({ [key]: v as number } as any)}
          />
          <TextField
            size="small"
            type="number"
            value={Number.isFinite(value) ? value : ''}
            disabled={disabled}
            onChange={(e) => {
              const v = parseFloat(e.target.value);
              if (Number.isFinite(v)) onChange({ [key]: v } as any);
            }}
            inputProps={{ step, min, max, style: { fontFamily: 'monospace' } }}
            sx={{ width: 118 }}
          />
        </Stack>
      </Box>
    ))}
  </Stack>
);

// ── the editor ──────────────────────────────────────────────────────────────

const ShortsFramingEditor: React.FC<Props> = ({ clipId, onClose }) => {
  const qc = useQueryClient();
  const [tab, setTab] = useState(0);
  const [params, setParams] = useState<ShortsRenderParams | null>(null);
  const [baseVersion, setBaseVersion] = useState<string | null>(null);
  const [force, setForce] = useState(false);
  const [openShot, setOpenShot] = useState<number | null>(null);
  // REAPER's review opens per-person; every cut is behind a toggle.
  const [everyCut, setEveryCut] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  // Set the instant the POST returns, so confirmation never depends on a
  // background refetch landing. Clicking Queue and seeing nothing change is
  // indistinguishable from the button not working.
  const [queuedJob, setQueuedJob] = useState<string | null>(null);
  // Re-rendering with no edits is legitimate — the last one failed, REAPER
  // changed underneath, the render options moved. But it is also what an
  // accidental click looks like, so it takes a second, deliberate click.
  const [confirmNoChanges, setConfirmNoChanges] = useState(false);
  // The caption we asked a preview for. The box can be typed in while the
  // preview is in flight, and showing a picture of text you have since changed
  // is worse than showing none — so the agent echoes the caption back and we
  // only render a preview that still matches.
  const [previewFor, setPreviewFor] = useState<string | null>(null);

  const q = useQuery({
    queryKey: ['shorts', 'render-params', clipId],
    queryFn: () => shortsAPI.getRenderParams(clipId!),
    enabled: !!clipId,
    // While a render is in flight, poll so the result lands without a reload.
    refetchInterval: (query) =>
      (((query.state.data as any)?.in_flight_job || queuedJob)
        ? 5000
        : ((query.state.data as any)?.jobs || []).some(
            (j: any) => j.kind === 'title_preview'
              && ['queued', 'rendering'].includes(j.status))
          ? 2000 : false),
  });

  const loaded = q.data;

  // Reset the working copy whenever the server's version changes identity —
  // a fresh clip, or a render that moved base_version under us.
  useEffect(() => {
    if (!queuedJob || !loaded) return;
    const j = (loaded.jobs || []).find((x) => x.job_id === queuedJob);
    // Once the job reaches a terminal state the dedicated alerts below say
    // what happened; the "Queued" banner has done its job.
    if (j && ['done', 'failed', 'conflict'].includes(j.status)) setQueuedJob(null);
  }, [loaded, queuedJob]);

  useEffect(() => {
    if (!loaded) return;
    setParams(loaded.render_params
      ? JSON.parse(JSON.stringify(loaded.render_params))
      : null);
    setBaseVersion(loaded.base_version ?? null);
    setForce(false);
    setErr(null);
    setConfirmNoChanges(false);
  }, [loaded?.clip_id, loaded?.base_version]); // eslint-disable-line react-hooks/exhaustive-deps

  const dirty = useMemo(() => {
    if (!params || !loaded?.render_params) return false;
    return JSON.stringify(params) !== JSON.stringify(loaded.render_params);
  }, [params, loaded?.render_params]);

  const stillByShot = useMemo(() => {
    const m = new Map<number, ShortsStill>();
    (loaded?.stills || []).forEach((s) => {
      if (typeof s.shot_index === 'number') m.set(s.shot_index, s);
    });
    return m;
  }, [loaded?.stills]);

  const shots: ShortsShot[] = params?.shots || [];
  const words: ShortsWord[] = params?.words || [];
  const cameras = useMemo(() => {
    const set = new Set<string>(Object.keys(params?.camera_framing || {}));
    shots.forEach((s) => s.camera && set.add(s.camera));
    return Array.from(set).sort();
  }, [params?.camera_framing, shots]);

  const editable = !!loaded?.editable;
  const inFlight = loaded?.in_flight_job || null;
  const locked = !editable || !!inFlight;

  // -- mutators. Each one mutates a CLONE and preserves every untouched key.

  const patchCamera = useCallback((camera: string, patch: Partial<ShortsShot>) => {
    setParams((prev) => {
      if (!prev) return prev;
      const next: ShortsRenderParams = JSON.parse(JSON.stringify(prev));
      const merged: any = { ...(next.camera_framing?.[camera] as any), ...patch };
      if (patch.scale !== undefined) merged.scale = clampScale(patch.scale);
      next.camera_framing = { ...(next.camera_framing || {}), [camera]: merged };
      patch = merged.scale !== undefined && patch.scale !== undefined
        ? { ...patch, scale: merged.scale } : patch;
      // Shots that aren't hand-framed carry a COPY of the camera default
      // rather than a reference to it, so the default alone would leave them
      // on the old numbers — the blob is sent wholesale and is what renders.
      next.shots = (next.shots || []).map((sh) =>
        sh.camera === camera && sh.custom !== true ? { ...sh, ...patch } : sh);
      return next;
    });
  }, []);

  /**
   * A SHORT-level override: this camera, this whole short, leaving the
   * episode-wide camera default alone.
   *
   * This is the third level REAPER has and we were missing (§24.2). The blob
   * has no short level of its own — REAPER records one as `custom: true` on
   * every one of that camera's shots — so that is exactly what we write.
   */
  const patchCameraShots = useCallback((camera: string,
                                        patch: Partial<ShortsShot>) => {
    setParams((prev) => {
      if (!prev) return prev;
      const next: ShortsRenderParams = JSON.parse(JSON.stringify(prev));
      next.shots = (next.shots || []).map((sh) => {
        if (sh.camera !== camera) return sh;
        const merged = { ...sh, ...patch, custom: true };
        if (patch.scale !== undefined) merged.scale = clampScale(patch.scale);
        return merged;
      });
      return next;
    });
  }, []);

  /** REAPER's "Same for later <camera> shots". */
  const copyToLaterShots = useCallback((index: number) => {
    setParams((prev) => {
      if (!prev) return prev;
      const next: ShortsRenderParams = JSON.parse(JSON.stringify(prev));
      const src = (next.shots || [])[index];
      if (!src) return prev;
      next.shots = (next.shots || []).map((sh, i) =>
        i > index && sh.camera === src.camera
          ? { ...sh, posX: src.posX, posY: src.posY, scale: src.scale,
              custom: true }
          : sh);
      return next;
    });
  }, []);

  const patchShot = useCallback((index: number, patch: Partial<ShortsShot>) => {
    setParams((prev) => {
      if (!prev) return prev;
      const next: ShortsRenderParams = JSON.parse(JSON.stringify(prev));
      const sh = (next.shots || [])[index];
      if (!sh) return prev;
      // Touching one shot's framing is by definition hand-framing it, so it
      // must stop tracking its camera default or the next default edit would
      // silently revert the change.
      const merged: any = { ...sh, ...patch, custom: true };
      if (patch.scale !== undefined) merged.scale = clampScale(patch.scale);
      next.shots![index] = merged;
      return next;
    });
  }, []);

  const resetShot = useCallback((index: number) => {
    setParams((prev) => {
      if (!prev) return prev;
      const next: ShortsRenderParams = JSON.parse(JSON.stringify(prev));
      const sh = (next.shots || [])[index];
      const def = next.camera_framing?.[sh?.camera];
      if (!sh || !def) return prev;
      next.shots![index] = {
        ...sh, posX: def.posX, posY: def.posY, scale: def.scale, custom: false,
      };
      return next;
    });
  }, []);

  const patchRender = useCallback((patch: Record<string, any>) => {
    setParams((prev) => prev
      ? { ...prev, render: { ...(prev.render || {}), ...patch } }
      : prev);
  }, []);

  const titlePreview = useMutation({
    mutationFn: () => shortsAPI.requestTitlePreview(clipId!, {
      caption: params?.caption ?? '',
      caption_style: params?.caption_style ?? null,
    }),
    onSuccess: () => { setPreviewFor(params?.caption ?? ''); q.refetch(); },
    onError: (e: any) => setErr(
      e?.response?.data?.detail || 'could not request a title preview'),
  });

  // The newest finished preview, but only if it is OF the caption on screen.
  const preview = useMemo(() => {
    const j = (loaded?.jobs || []).find(
      (x) => x.kind === 'title_preview' && x.status === 'done');
    if (!j) return null;
    return previewFor !== null && previewFor !== (params?.caption ?? '')
      ? null : j;
  }, [loaded?.jobs, previewFor, params?.caption]);

  const previewPending = (loaded?.jobs || []).some(
    (x) => x.kind === 'title_preview' && ['queued', 'rendering'].includes(x.status));

  const queue = useMutation({
    mutationFn: () => shortsAPI.requestRerender(clipId!, {
      // The WHOLE blob, unknown keys and all.
      render_params: params!,
      base_version: baseVersion,
      force,
    }),
    onSuccess: (job: any) => {
      setErr(null);
      setQueuedJob(job?.job_id ?? 'queued');
      qc.invalidateQueries({ queryKey: ['shorts'] });
      q.refetch();
    },
    onError: (e: any) => setErr(
      e?.response?.data?.detail || e?.message || 'could not queue the render'),
  });

  const customCount = shots.filter((s) => s.custom === true).length;

  return (
    <Dialog open={!!clipId} onClose={onClose} maxWidth="lg" fullWidth
            PaperProps={{ sx: { height: '92vh' } }}>
      <DialogTitle sx={{ pb: 1 }}>
        <Stack direction="row" alignItems="center" spacing={1}>
          <Movie fontSize="small" />
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography variant="h6" noWrap>
              {loaded?.title || 'Re-render'}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {params?.caption ? `“${params.caption}” · ` : ''}
              {shots.length} shots · {words.length} words
              {baseVersion ? ` · base ${baseVersion}` : ''}
            </Typography>
          </Box>
          <IconButton size="small" onClick={onClose}><Close fontSize="small" /></IconButton>
        </Stack>
      </DialogTitle>

      <DialogContent dividers sx={{ p: 0 }}>
        {q.isLoading && <LinearProgress />}
        {q.isError && (
          <Alert severity="error" sx={{ m: 2 }}>
            Could not load this clip's render parameters.
          </Alert>
        )}

        <Box sx={{ px: 2, pt: 2 }}>
          {loaded && !loaded.render_params && (
            <Alert severity="warning" icon={<Warning />}>
              <AlertTitle>No render parameters for this clip</AlertTitle>
              It was ingested before the exporter started sending
              <code> render_params</code>, so there is nothing to edit and
              REAPER has no region to re-render from. Re-export it from the
              exporter and it will arrive with everything this editor needs.
            </Alert>
          )}

          {loaded && !loaded.editable && (
            <Alert severity="error">
              <AlertTitle>
                Already published on {loaded.published.join(', ')}
              </AlertTitle>
              Re-rendering can't change a live post — YouTube can't replace a
              video's bytes, so a fix would mean delete and re-upload, losing
              the URL, the views and the comments. Fix it on the platform.
            </Alert>
          )}

          {loaded?.editable && loaded.handed_over.length > 0 && (
            <Alert severity="warning" icon={<Warning />}>
              <AlertTitle>
                {loaded.handed_over.join(', ')} already
                {loaded.handed_over.length === 1 ? ' has' : ' have'} the file
              </AlertTitle>
              Those platforms hold their own timer, so the upload happened
              ahead of the slot. A new render lands in S3 but the{' '}
              <strong>old cut still releases</strong> there. To fix it on those
              platforms, delete the scheduled post and retry the target.
              <FormControlLabel
                sx={{ display: 'block', mt: 1 }}
                control={<Checkbox size="small" checked={force}
                                   onChange={(e) => setForce(e.target.checked)} />}
                label={
                  <Typography variant="body2">
                    Re-render anyway — I understand{' '}
                    {loaded.handed_over.join(', ')} will release the old cut
                  </Typography>
                }
              />
            </Alert>
          )}

          {inFlight && (
            <Alert severity="info" sx={{ mt: 1 }}
                   icon={<CircularProgress size={18} />}>
              <AlertTitle sx={{ mb: 0 }}>
                A render is {inFlight.status}
              </AlertTitle>
              Queued {inFlight.requested_at
                ? new Date(inFlight.requested_at).toLocaleString() : ''}.
              Editing is locked until it reports back; this updates itself.
            </Alert>
          )}

          {(loaded?.jobs || []).slice(0, 1).map((j) => (
            j.status === 'conflict' ? (
              <Alert severity="warning" sx={{ mt: 1 }} key={j.job_id}>
                <AlertTitle>REAPER moved on — last render was a conflict</AlertTitle>
                The region was re-exported on the Mac after this edit started,
                so nothing was rendered rather than overwriting that work.
                Reload to start from REAPER's current parameters.
                <Button size="small" sx={{ ml: 1 }} onClick={() => q.refetch()}>
                  Reload
                </Button>
              </Alert>
            ) : j.status === 'failed' ? (
              <Alert severity="error" sx={{ mt: 1 }} key={j.job_id}>
                <AlertTitle>The last render failed</AlertTitle>
                {j.error || 'no reason reported'}
              </Alert>
            ) : null
          ))}

          {queuedJob && !inFlight && !(loaded?.jobs || [])
            .some((j) => j.job_id === queuedJob && j.status !== 'queued') && (
            <Alert severity="success" sx={{ mt: 1 }}>
              <AlertTitle>Queued</AlertTitle>
              Sent to the Mac. The agent polls every minute, so it usually
              starts within one.
              <br /><br />
              <strong>You can close this window.</strong> The clip's row in the
              batch shows the stage — queued, rendering, re-rendered or failed
              — and the reason if it fails. Leaving it open works too; it
              updates itself.
            </Alert>
          )}

          {err && <Alert severity="error" sx={{ mt: 1 }}>{err}</Alert>}
        </Box>

        {params && (
          <>
            <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ px: 2, mt: 1 }}>
              <Tab icon={<Tune fontSize="small" />} iconPosition="start"
                   label={`Framing (${customCount} hand-framed)`} />
              <Tab icon={<Subtitles fontSize="small" />} iconPosition="start"
                   label="Transcript" />
              <Tab label="Render options" />
              <Tab label="Caption & title" />
            </Tabs>
            <Divider />

            {/* ── Framing ─────────────────────────────────────────────── */}
            {tab === 0 && (
              <Box sx={{ p: 2 }}>
                <Alert severity="info" variant="outlined" sx={{ mb: 2 }}>
                  This is the output as it will render. <strong>Drag the
                  picture</strong> to move the person — the same gesture as
                  REAPER's framing window, so a framing set here means the same
                  thing there. Zoom can't go below cover-fill
                  ({COVER_MIN_SCALE.toFixed(2)}%), because REAPER clamps to it
                  and the frame would otherwise show black.
                </Alert>

                <Typography variant="subtitle2" gutterBottom>
                  Camera framing — all shorts in this episode
                </Typography>
                <Alert severity="warning" variant="outlined" sx={{ mb: 2 }}>
                  <AlertTitle>These reach beyond this short</AlertTitle>
                  A camera default is stored per camera for the whole episode,
                  so changing one here changes that camera in{' '}
                  <strong>every other short of this episode</strong> the next
                  time those are exported. It also moves this clip's shots on
                  that camera that haven't been hand-framed.
                  <br /><br />
                  To change only this short, edit it under “This short” below:
                  that writes an override and leaves the episode alone.
                </Alert>
                <Box sx={{
                  display: 'grid', gap: 2, mb: 3,
                  gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
                }}>
                  {cameras.map((cam) => {
                    const def = params.camera_framing?.[cam];
                    const firstShot = shots.findIndex((s) => s.camera === cam);
                    const level = framingLevel(shots as any, cam);
                    if (!def) return null;
                    return (
                      <Paper key={cam} variant="outlined" sx={{ p: 1.5 }}>
                        <Stack direction="row" alignItems="center" spacing={1}
                               sx={{ mb: 1 }}>
                          <Typography variant="subtitle2">{cam}</Typography>
                          <Chip size="small" variant="outlined"
                                color={level === 'camera' ? 'default' : 'warning'}
                                label={FRAMING_LABEL[level]} />
                        </Stack>
                        <Stack direction="row" spacing={1.5}>
                          <PhoneFrame
                            still={firstShot >= 0 ? stillByShot.get(firstShot) : undefined}
                            posX={def.posX} posY={def.posY} scale={def.scale}
                            disabled={locked}
                            onChange={(posX, posY) => patchCamera(cam, { posX, posY })}
                          />
                          <Box sx={{ flex: 1, minWidth: 0 }}>
                            <FramingControls
                              posX={def.posX} posY={def.posY} scale={def.scale}
                              disabled={locked}
                              onChange={(patch) => patchCamera(cam, patch)}
                            />
                          </Box>
                        </Stack>
                      </Paper>
                    );
                  })}
                </Box>

                {/* REAPER's review window: per-person first, every cut behind
                    a toggle. 63 rows became about 5 per person. */}
                <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
                  <Typography variant="subtitle2" sx={{ flex: 1 }}>
                    This short
                  </Typography>
                  <FormControlLabel
                    control={<Checkbox size="small" checked={everyCut}
                                       onChange={(e) => setEveryCut(e.target.checked)} />}
                    label={
                      <Typography variant="body2">
                        Show every cut ({shots.length}) — to fix one shot only
                      </Typography>
                    }
                  />
                </Stack>

                {!everyCut && (
                  <Stack spacing={1}>
                    {cameras.map((cam) => {
                      const mine = shots
                        .map((sh, i) => ({ sh, i }))
                        .filter(({ sh }) => sh.camera === cam);
                      if (!mine.length) return null;
                      const onScreen = mine.reduce(
                        (t, { sh }) => t + Math.max(0, (sh.end || 0) - (sh.start || 0)), 0);
                      // REAPER samples the 5 longest shots per camera.
                      const samples = [...mine]
                        .sort((a, b) => ((b.sh.end - b.sh.start) - (a.sh.end - a.sh.start)))
                        .slice(0, 5)
                        .sort((a, b) => a.i - b.i);
                      const level = framingLevel(shots as any, cam);
                      const lead = samples[0];
                      return (
                        <Paper key={cam} variant="outlined" sx={{ p: 1.5 }}>
                          <Stack direction="row" alignItems="center" spacing={1}
                                 sx={{ mb: 1 }}>
                            <Typography variant="subtitle2">{cam}</Typography>
                            <Chip size="small" variant="outlined"
                                  color={level === 'camera' ? 'default' : 'warning'}
                                  label={FRAMING_LABEL[level]} />
                            <Typography variant="caption" color="text.secondary">
                              {mine.length} shot{mine.length === 1 ? '' : 's'},{' '}
                              {onScreen.toFixed(1)}s on screen
                            </Typography>
                            <Box sx={{ flex: 1 }} />
                            <Button
                              size="small" startIcon={<RestartAlt />}
                              disabled={locked || level === 'camera'}
                              onClick={() => mine.forEach(({ i }) => resetShot(i))}
                            >
                              Back to camera framing
                            </Button>
                          </Stack>
                          <Stack direction="row" spacing={1.5} alignItems="flex-start">
                            <Stack direction="row" spacing={0.75}
                                   sx={{ overflowX: 'auto', pb: 0.5 }}>
                              {samples.map(({ sh, i }) => (
                                <Box key={i}>
                                  <PhoneFrame
                                    still={stillByShot.get(i)}
                                    posX={sh.posX} posY={sh.posY} scale={sh.scale}
                                    disabled={locked} compact
                                    onChange={(posX, posY) =>
                                      patchCameraShots(cam, { posX, posY })}
                                  />
                                  <Typography variant="caption" color="text.secondary"
                                              sx={{ display: 'block', textAlign: 'center' }}>
                                    {fmtTime(sh.start)}
                                  </Typography>
                                </Box>
                              ))}
                            </Stack>
                            <Box sx={{ flex: 1, minWidth: 180 }}>
                              {lead && (
                                <FramingControls
                                  posX={lead.sh.posX} posY={lead.sh.posY}
                                  scale={lead.sh.scale} disabled={locked}
                                  onChange={(patch) => patchCameraShots(cam, patch)}
                                />
                              )}
                              <Typography variant="caption" color="text.secondary"
                                          sx={{ display: 'block', mt: 1 }}>
                                Edits here apply to all {mine.length} of {cam}'s
                                shots in this short, and leave the episode alone.
                              </Typography>
                            </Box>
                          </Stack>
                        </Paper>
                      );
                    })}
                  </Stack>
                )}

                {everyCut && (
                  <Stack spacing={0.5}>
                    {shots.map((sh, i) => {
                      const open = openShot === i;
                      const custom = sh.custom === true;
                      return (
                        <Paper key={i} variant="outlined"
                               sx={{ p: 1, bgcolor: open ? 'action.hover' : undefined }}>
                          <Stack direction="row" alignItems="center" spacing={1}>
                            <Typography variant="caption"
                                        sx={{ width: 28, fontFamily: 'monospace' }}>
                              {i}
                            </Typography>
                            <Typography variant="body2"
                                        sx={{ width: 130, fontFamily: 'monospace' }}>
                              {fmtTime(sh.start)}–{fmtTime(sh.end)}
                            </Typography>
                            <Chip size="small" label={sh.camera} />
                            <Chip size="small" variant="outlined"
                                  color={custom ? 'warning' : 'default'}
                                  label={custom ? FRAMING_LABEL.shot
                                                : FRAMING_LABEL.camera} />
                            <Box sx={{ flex: 1 }} />
                            <Typography variant="caption" color="text.secondary"
                                        sx={{ fontFamily: 'monospace' }}>
                              {sh.posX?.toFixed(4)} / {sh.posY?.toFixed(4)} / {sh.scale}
                            </Typography>
                            <Button size="small"
                                    onClick={() => setOpenShot(open ? null : i)}>
                              {open ? 'Close' : 'Frame'}
                            </Button>
                          </Stack>
                          {open && (
                            <Stack direction="row" spacing={2} sx={{ mt: 1.5 }}
                                   alignItems="flex-start">
                              <ShotStrip
                                still={stillByShot.get(i)}
                                posX={sh.posX} posY={sh.posY} scale={sh.scale}
                                disabled={locked}
                                onChange={(posX, posY) => patchShot(i, { posX, posY })}
                              />
                              <Box sx={{ flex: 1, minWidth: 200 }}>
                                <FramingControls
                                  posX={sh.posX} posY={sh.posY} scale={sh.scale}
                                  disabled={locked}
                                  onChange={(patch) => patchShot(i, patch)}
                                />
                                <Stack direction="row" spacing={1} sx={{ mt: 1.5 }}
                                       flexWrap="wrap">
                                  <Button
                                    size="small" startIcon={<RestartAlt />}
                                    disabled={locked || !custom
                                              || !params.camera_framing?.[sh.camera]}
                                    onClick={() => resetShot(i)}
                                  >
                                    Back to camera framing
                                  </Button>
                                  <Button
                                    size="small"
                                    disabled={locked}
                                    onClick={() => copyToLaterShots(i)}
                                  >
                                    Same for later {sh.camera} shots
                                  </Button>
                                </Stack>
                              </Box>
                            </Stack>
                          )}
                        </Paper>
                      );
                    })}
                  </Stack>
                )}
              </Box>
            )}

            {/* ── Transcript ──────────────────────────────────────────── */}
            {tab === 1 && (
              <Box sx={{ p: 2 }}>
                <Alert severity="warning" icon={<Warning />} sx={{ mb: 2 }}>
                  <AlertTitle>These edits reach past this short</AlertTitle>
                  Transcript changes are written straight into the{' '}
                  <strong>shared episode transcript</strong> on the Mac, not
                  just this clip — so a fix here fixes it everywhere, and a typo
                  here spreads too. Speakers and timings are now editable as
                  well, and they drive caption sync — a line you move moves its
                  captions with it.
                </Alert>
                <TranscriptScriptEditor
                  words={words}
                  originalWords={loaded?.render_params?.words}
                  disabled={locked}
                  onChange={(next) => setParams((prev) =>
                    prev ? { ...prev, words: next } : prev)}
                />
              </Box>
            )}

            {/* ── Render options ──────────────────────────────────────── */}
            {tab === 2 && (
              <Box sx={{ p: 2, maxWidth: 560 }}>
                <Stack spacing={1}>
                  <FormControlLabel
                    control={<Switch checked disabled />}
                    label={
                      <Stack>
                        <Typography variant="body2">Bleep profanity</Typography>
                        <Typography variant="caption" color="text.secondary">
                          Locked on. The pipeline refuses a render that doesn't
                          assert bleeping, because "no bleep" output is
                          indistinguishable by filename.
                        </Typography>
                      </Stack>
                    }
                  />
                  <Divider />
                  {([
                    ['captions', 'Burn in captions'],
                    ['grade', 'Colour grade'],
                    ['teeth', 'Teeth retouch'],
                    ['eyepop', 'Eye pop'],
                    ['judder_repair', 'Rebuild juddered frames (24.000 vs 23.976 fps)'],
                    ['avoid_judder', 'Cut away from a juddering camera'],
                  ] as const).map(([key, label]) => (
                    <FormControlLabel
                      key={key}
                      control={
                        <Switch
                          checked={!!params.render?.[key]}
                          disabled={locked}
                          onChange={(e) => patchRender({ [key]: e.target.checked })}
                        />
                      }
                      label={<Typography variant="body2">{label}</Typography>}
                    />
                  ))}
                  {params.render?.eyepop && (
                    <Box sx={{ pl: 4 }}>
                      <Typography variant="caption">
                        Eye pop strength: {params.render?.eyepop_pct ?? 100}%
                      </Typography>
                      <Slider
                        size="small" min={0} max={100} step={5}
                        value={Number(params.render?.eyepop_pct ?? 100)}
                        disabled={locked}
                        onChange={(_, v) => patchRender({ eyepop_pct: v as number })}
                      />
                    </Box>
                  )}
                  <TextField
                    select size="small" label="Beauty preset"
                    value={params.render?.beauty ?? ''}
                    disabled={locked}
                    onChange={(e) => patchRender({ beauty: e.target.value })}
                    sx={{ maxWidth: 260, mt: 1 }}
                  >
                    {['none', 'gp_white', 'gp_warm', 'gp_soft']
                      .concat(
                        params.render?.beauty
                        && !['none', 'gp_white', 'gp_warm', 'gp_soft']
                          .includes(params.render.beauty)
                          ? [params.render.beauty] : [])
                      .map((v) => <MenuItem key={v} value={v}>{v}</MenuItem>)}
                  </TextField>
                  <Typography variant="caption" color="text.secondary">
                    Engine: <code>{params.render?.engine ?? 'reaper'}</code>.
                    Any option REAPER sends that isn't shown here is passed back
                    untouched rather than dropped.
                  </Typography>
                </Stack>
              </Box>
            )}

            {/* ── Caption & title ─────────────────────────────────────── */}
            {tab === 3 && (
              <Box sx={{ p: 2, maxWidth: 640 }}>
                <Stack spacing={2}>
                  <TextField
                    label="On-screen caption (burned into the video)" fullWidth
                    value={params.caption ?? ''}
                    disabled={locked}
                    onChange={(e) => setParams((p) =>
                      // `|` separates the parts of REAPER's region name, which
                      // is how every output file gets named. It would be
                      // silently replaced with `-` on their side, so strip it
                      // here where the user can see it happen.
                      p ? { ...p, caption: e.target.value.replace(/[|｜]/g, '-') } : p)}
                    error={!!params.caption && !/^[\x20-\x7E\u00A0-\u024F]*$/.test(params.caption)}
                    helperText={
                      params.caption && !/^[\x20-\x7E\u00A0-\u024F]*$/.test(params.caption)
                        ? 'The title box font is Montserrat Bold — emoji and '
                          + 'non-Latin characters will not render.'
                        : `The title box, shown for the first 4 seconds. `
                          + `${(params.caption || '').length}/40 characters `
                          + `reads as one or two lines; longer still fits but `
                          + `gets small. Changing this needs a re-render.`
                    }
                  />
                  <Stack direction="row" spacing={1} alignItems="center">
                    <Button
                      size="small" variant="outlined"
                      startIcon={previewPending
                        ? <CircularProgress size={14} /> : <Subtitles />}
                      disabled={!params.caption || previewPending
                                || titlePreview.isPending}
                      onClick={() => titlePreview.mutate()}
                    >
                      {previewPending ? 'Laying it out…' : 'Preview the title box'}
                    </Button>
                    <Typography variant="caption" color="text.secondary">
                      About a second — it draws the box over this short's
                      frame without rendering the video.
                    </Typography>
                  </Stack>

                  {preview && (
                    <Paper variant="outlined" sx={{ p: 1.5 }}>
                      <Stack direction="row" spacing={2} alignItems="flex-start">
                        {preview.preview_url && (
                          <Box component="img" src={preview.preview_url}
                               alt="title box preview"
                               sx={{ width: 150, borderRadius: 1,
                                     border: '1px solid', borderColor: 'divider' }} />
                        )}
                        <Box>
                          {preview.fits === false ? (
                            <Alert severity="warning" sx={{ mb: 1 }}>
                              <AlertTitle>Too long for the box</AlertTitle>
                              It had to shrink below 75% or wrap past three
                              lines. Shorten it and preview again.
                            </Alert>
                          ) : (
                            <Alert severity="success" sx={{ mb: 1 }}>It fits.</Alert>
                          )}
                          {preview.title_lines && (
                            <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                              {preview.title_lines.map((l, i) => (
                                <Box component="span" key={i} sx={{ display: 'block' }}>
                                  {l}
                                </Box>
                              ))}
                            </Typography>
                          )}
                          <Typography variant="caption" color="text.secondary">
                            {preview.title_lines?.length ?? 0} line
                            {preview.title_lines?.length === 1 ? '' : 's'}
                            {preview.title_font_px
                              ? ` · ${preview.title_font_px}px` : ''}
                          </Typography>
                        </Box>
                      </Stack>
                    </Paper>
                  )}

                  <TextField
                    label="Render title" fullWidth
                    value={params.title ?? ''}
                    disabled={locked}
                    onChange={(e) => setParams((p) =>
                      p ? { ...p, title: e.target.value } : p)}
                    helperText="The short's name — this is what becomes the post title on every platform (YouTube adds #actualplay #ttrpgpodcast). It is NOT the burned-in text above."
                  />
                  <Alert severity="info" variant="outlined">
                    Changing either of these needs a re-render to take effect,
                    because they're drawn into the video.
                  </Alert>
                </Stack>
              </Box>
            )}
          </>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 2, py: 1.5 }}>
        <Typography variant="caption" color="text.secondary" sx={{ flex: 1 }}>
          {dirty
            ? 'Unsaved edits — nothing happens until you queue the re-render.'
            : confirmNoChanges
              ? 'Nothing has been changed. Click again to re-render this clip '
                + 'exactly as it is — useful after a failed render.'
              : 'No changes. You can still re-render as-is.'}
          {loaded?.needs_force && !force ? ' Acknowledge the warning above to continue.' : ''}
        </Typography>
        <Button onClick={onClose}>Close</Button>
        <Tooltip title={
          !editable ? 'Published — re-rendering is refused'
            : inFlight ? 'A render is already in flight'
              : !dirty ? 'Make a change first'
                : loaded?.needs_force && !force
                  ? 'Tick the acknowledgement above'
                  : 'Queues the job; the Mac agent picks it up on its next poll'
        }>
          <span>
            <Button
              variant="contained"
              color={!dirty && confirmNoChanges ? 'warning' : 'primary'}
              startIcon={<Send />}
              disabled={locked || queue.isPending
                        || (!!loaded?.needs_force && !force)}
              onClick={() => {
                if (!dirty && !confirmNoChanges) {
                  setConfirmNoChanges(true);
                  return;
                }
                queue.mutate();
              }}
            >
              {queue.isPending
                ? 'Queueing…'
                : !dirty && confirmNoChanges
                  ? 'Yes — re-render unchanged'
                  : 'Queue re-render'}
            </Button>
          </span>
        </Tooltip>
      </DialogActions>
    </Dialog>
  );
};

export default ShortsFramingEditor;
